import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Import all images dynamically
const importAll = (r) => r.keys().map(r);
const allImages = importAll(require.context('../assets/images', false, /\.(webp)$/));

const FullGallery = () => {
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleDownloadAll = async () => {
        setIsDownloading(true);
        const zip = new JSZip();

        try {
            const promises = allImages.map(async (src, index) => {
                const response = await fetch(src);
                const blob = await response.blob();
                const filename = `sachupreksha-gallery${index + 1}.webp`;
                zip.file(filename, blob);
            });

            await Promise.all(promises);
            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "sachupreksha-gallery.zip");
        } catch (error) {
            console.error("Error downloading images:", error);
            alert("Failed to download images. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <section className="py-12 px-4 md:px-8 bg-wedding-ivory min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-12">
                    <Link to="/" className="text-wedding-gold hover:text-wedding-charcoal transition-colors font-serif italic text-lg">
                        ← Back to Home
                    </Link>
                    <button
                        onClick={handleDownloadAll}
                        disabled={isDownloading}
                        className="text-wedding-gold hover:text-wedding-charcoal transition-colors font-serif italic text-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDownloading ? 'Downloading...' : 'Download All'}
                        {!isDownloading && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M12 3v13.5M8.25 12.75 12 16.5l3.75-3.75" />
                            </svg>
                        )}
                    </button>
                </div>

                <Reveal>
                    <div className="text-center mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs text-wedding-gold font-bold mb-4 block">All Memories</span>
                        <h2 className="font-serif text-4xl md:text-6xl text-wedding-charcoal">Gallery</h2>
                    </div>
                </Reveal>

                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
                    {allImages.map((src, index) => (
                        <Reveal key={index} delay={(index % 3) * 100} className="break-inside-avoid mb-6">
                            <div className="overflow-hidden group relative shadow-md hover:shadow-xl transition-all duration-500 rounded-lg">
                                <img
                                    src={src}
                                    alt={`Gallery ${index + 1}`}
                                    className="w-full h-auto object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-wedding-charcoal/0 group-hover:bg-wedding-charcoal/10 transition-all duration-500"></div>
                                <a
                                    href={src}
                                    download={`gallery-image-${index + 1}`}
                                    className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-wedding-charcoal p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 shadow-lg z-10 cursor-pointer"
                                    title="Download Image"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M12 3v13.5M8.25 12.75 12 16.5l3.75-3.75" />
                                    </svg>
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <Link to="/" className="inline-block px-8 py-3 border border-wedding-gold text-wedding-gold hover:bg-wedding-gold hover:text-white transition-all duration-300 font-serif italic">
                        Return to Home
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default FullGallery;
